import { useEffect, useState } from 'react';
import isEqual from 'lodash.isequal';
import groupBy from 'lodash.groupby';
import IconChangeLogUpdated from '../icons/change-log-updated';
import IconChangeLogAdded from '../icons/change-log-added';
import IconChangeLogDeleted from '../icons/change-log-deleted';
import formatDate from '../../helpers/format-date';
import { ChangeLogTypes } from './types';

import styles from './change-logs.module.css';

export default function ChangeLog({ changeLog, users }: ChangeLogTypes) {
  const [updatedValues, setUpdatedValues] = useState<
    {
      name: string;
      prev: string | any;
      current: string | any;
      prevType?: 'string' | 'object' | 'objectArray';
      currentType?: 'string' | 'object' | 'objectArray';
    }[]
  >([]);

  useEffect(() => {
    if (changeLog) {
      const fields: { name: string }[] = [];
      const prevChangeLogs = changeLog.prev;
      const currentChangeLogs = changeLog.current;

      Object.keys(currentChangeLogs)?.forEach((item) => {
        if (
          (prevChangeLogs &&
            prevChangeLogs[item]?.toString() &&
            currentChangeLogs[item]?.toString() &&
            typeof currentChangeLogs[item] !== 'object' &&
            !isEqual(
              currentChangeLogs[item]?.toString(),
              prevChangeLogs[item]?.toString(),
            )) ||
          (prevChangeLogs &&
            prevChangeLogs[item]?.toString() &&
            currentChangeLogs[item]?.toString() &&
            typeof currentChangeLogs[item] === 'object' &&
            !isEqual(currentChangeLogs[item], prevChangeLogs[item])) ||
          (prevChangeLogs &&
            !prevChangeLogs[item]?.toString() &&
            currentChangeLogs[item]?.toString()) ||
          (!prevChangeLogs &&
            currentChangeLogs &&
            currentChangeLogs[item]?.toString())
        ) {
          fields.push({
            name: item,
          });
        }
      });

      if (fields?.length) {
        const updatedFieldsValues: {
          name: string;
          prev: string | any;
          current: string | any;
          prevType?: 'string' | 'object' | 'objectArray';
          currentType?: 'string' | 'object' | 'objectArray';
        }[] = [];

        fields?.forEach((item) => {
          let prev: string | any = '-';
          let current: string | any = '-';
          let prevType: 'string' | 'object' | 'objectArray' = 'string';
          let currentType: 'string' | 'object' | 'objectArray' = 'string';

          if (changeLog?.prev && changeLog?.prev[item.name]?.toString()) {
            if (Array.isArray(changeLog?.prev[item.name])) {
              if (typeof changeLog?.prev[item.name][0] === 'object') {
                const objectsFieldsArray: any[] = [];

                changeLog?.prev[item.name]?.forEach((itemValue: any) => {
                  objectsFieldsArray.push(itemValue);
                });

                if (item.name === 'periods') {
                  const grouped = groupBy(
                    objectsFieldsArray,
                    ({ weekDay }: { weekDay: string }) => weekDay,
                  );

                  prev = grouped;
                } else {
                  prev = objectsFieldsArray;
                }

                prevType = 'objectArray';
              } else {
                prev = changeLog?.prev[item.name]?.join(', ');
                prevType = 'string';
              }
            } else if (typeof changeLog?.prev[item.name] === 'object') {
              prevType = 'object';
              prev = changeLog?.prev[item.name];
            } else {
              prev = changeLog?.prev[item.name];
              prevType = 'string';
            }
          }

          if (changeLog?.current[item.name]?.toString()) {
            if (Array.isArray(changeLog?.current[item.name])) {
              if (typeof changeLog?.current[item.name][0] === 'object') {
                const objectsFieldsArray: any[] = [];

                changeLog?.current[item.name]?.forEach((itemValue: any) => {
                  objectsFieldsArray.push(itemValue);
                });

                if (item.name === 'periods') {
                  const grouped = groupBy(
                    objectsFieldsArray,
                    ({ weekDay }: { weekDay: string }) => weekDay,
                  );

                  current = grouped;
                } else {
                  current = objectsFieldsArray;
                }

                currentType = 'objectArray';
              } else {
                current = changeLog?.current[item.name]?.join(', ');
                currentType = 'string';
              }
            } else if (typeof changeLog?.current[item.name] === 'object') {
              currentType = 'object';
              current = changeLog?.current[item.name];
            } else {
              current = changeLog?.current[item.name];
              currentType = 'string';
            }
          }

          updatedFieldsValues.push({
            name: item.name,
            prev,
            current,
            prevType,
            currentType,
          });
        });

        setUpdatedValues(updatedFieldsValues);
      }
    }
  }, [changeLog]);

  return (
    <div className={styles.changeLogItem}>
      {updatedValues?.map((item) => (
        <div key={item.name}>
          {item.prevType === 'string' && item.currentType === 'string' && (
            <div className={styles.selectedField} key={item.name}>
              <div className={styles.selectedFieldName}>
                <div className={styles.selectedFieldNameLeft}>{item.name}</div>
                <div className={styles.selectedFieldNameRight}>
                  <div className={styles.employeeName}>
                    {changeLog?.employeeId &&
                    changeLog?.employeeId !== 'undefined' &&
                    changeLog?.employeeId !== 'null' &&
                    users
                      ? users?.find(
                          (user) => user.value === changeLog.employeeId,
                        )?.name
                      : ''}
                  </div>
                  <div className={styles.createdDate}>
                    {formatDate(changeLog?.createdAt as string, true)}
                  </div>
                </div>
              </div>

              <div className={styles.selectedFieldChangesWrapper}>
                <div className={styles.selectedFieldChanges}>
                  <div className={styles.selectedFieldChangeValueWrapper}>
                    <span className={styles.selectedFieldChangeValue}>
                      {item.prev.toString()}
                    </span>
                  </div>

                  <div className={styles.selectedFieldChangesSeparatorWrapper}>
                    <div className={styles.selectedFieldArrowWrapper}>
                      {item.prev &&
                        item.prev !== '-' &&
                        item.prev.toString() &&
                        item.current &&
                        item.current.toString() && <IconChangeLogUpdated />}
                      {item.prev === '-' && item.current.toString() && (
                        <IconChangeLogAdded />
                      )}
                      {item.prev &&
                        item.prev !== '-' &&
                        !item.current.toString() && <IconChangeLogDeleted />}
                    </div>
                    <div className={styles.selectedFieldChangesSeparator} />
                  </div>
                  <div className={styles.selectedFieldChangeValueWrapper}>
                    <span className={styles.selectedFieldChangeValue}>
                      {item?.current.toString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          {item.currentType === 'objectArray' &&
            item.name === 'periods' &&
            item.current &&
            Object.keys(item.current)?.length &&
            Object.keys(item.current)?.map((value: any, index: number) => {
              const objectArrayContent = (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${item.current[value]}-${index}`}
                  className={styles.selectedField}
                >
                  {((item.prev && !isEqual(item.prev, item.current)) ||
                    !item.prev) && (
                    <>
                      <div className={styles.selectedFieldName}>
                        <div className={styles.selectedFieldNameLeft}>
                          {item.name}
                        </div>
                        <div className={styles.selectedFieldNameRight}>
                          <div className={styles.employeeName}>
                            {changeLog?.employeeId &&
                            changeLog?.employeeId !== 'undefined' &&
                            changeLog?.employeeId !== 'null' &&
                            users
                              ? users?.find(
                                  (user) => user.value === changeLog.employeeId,
                                )?.name
                              : ''}
                          </div>
                          <div className={styles.createdDate}>
                            {formatDate(changeLog?.createdAt as string, true)}
                          </div>
                        </div>
                      </div>
                      <div className={styles.selectedFieldChangesWrapper}>
                        <div className={styles.weekDay}>
                          {value.toLowerCase()}
                        </div>
                        {item.current[value]?.map((period: any) => {
                          const currentPrev = item.prev
                            ? item.prev[value]?.find(
                                (prev: any) => prev.id === period.id,
                              )
                            : undefined;

                          return (
                            <div
                              className={`${styles.selectedFieldChanges} ${styles.period}`}
                              key={period.id}
                            >
                              <div
                                className={
                                  styles.selectedFieldChangeValueWrapper
                                }
                              >
                                <span
                                  className={styles.selectedFieldChangeValue}
                                >
                                  {currentPrev
                                    ? `${currentPrev.startTime} - ${currentPrev.endTime}`
                                    : '-'}
                                </span>
                              </div>
                              <div
                                className={
                                  styles.selectedFieldChangesSeparatorWrapper
                                }
                              >
                                <div
                                  className={styles.selectedFieldArrowWrapper}
                                >
                                  {currentPrev &&
                                    currentPrev !== '-' &&
                                    currentPrev &&
                                    period && <IconChangeLogUpdated />}
                                  {(currentPrev === '-' || !currentPrev) &&
                                    period.toString() && <IconChangeLogAdded />}
                                  {currentPrev &&
                                    currentPrev !== '-' &&
                                    !period.toString() && (
                                      <IconChangeLogDeleted />
                                    )}
                                </div>
                                <div
                                  className={
                                    styles.selectedFieldChangesSeparator
                                  }
                                />
                              </div>
                              <div
                                className={
                                  styles.selectedFieldChangeValueWrapper
                                }
                              >
                                <span
                                  className={styles.selectedFieldChangeValue}
                                >
                                  {period
                                    ? `${period.startTime} - ${period.endTime}`
                                    : '-'}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              );

              return objectArrayContent;
            })}
          {item.currentType === 'objectArray' &&
            item.name !== 'periods' &&
            (item.current as any[])?.map((value) => {
              const currentPrev = Array.isArray(item?.prev)
                ? item?.prev?.find((prev: any) => value.id === prev.id)
                : '';

              let objectArrayContent = <div key={value.id} />;

              const updatedFields: any = {};

              Object.keys(value).forEach((current: any) => {
                if (
                  (currentPrev && currentPrev[current] !== value[current]) ||
                  !currentPrev
                ) {
                  updatedFields[current] = {
                    prev: currentPrev[current] || '-',
                    current: value[current] || '-',
                  };
                }
              });

              if (Object.keys(updatedFields).length) {
                objectArrayContent = (
                  <div key={value.id}>
                    {Object.keys(updatedFields).map(
                      (updatedKey: any, index) => (
                        <div
                          className={styles.selectedField}
                          // eslint-disable-next-line react/no-array-index-key
                          key={`${updatedKey}-${index}`}
                        >
                          <div className={styles.selectedFieldName}>
                            {`${item.name}: ${updatedKey}`}
                          </div>

                          <div className={styles.selectedFieldChangesWrapper}>
                            <div className={styles.selectedFieldChanges}>
                              <div className={styles.selectedFieldChangeValue}>
                                {updatedFields[updatedKey]?.prev?.toString()}
                              </div>
                              <div
                                className={
                                  styles.selectedFieldChangesSeparatorWrapper
                                }
                              >
                                <div
                                  className={styles.selectedFieldArrowWrapper}
                                >
                                  {updatedFields[updatedKey].prev !== '-' &&
                                    updatedFields[updatedKey]?.current !==
                                      '-' && <IconChangeLogUpdated />}
                                  {updatedFields[updatedKey].prev === '-' &&
                                    updatedFields[updatedKey]?.current !==
                                      '-' && <IconChangeLogAdded />}
                                  {updatedFields[updatedKey].prev !== '-' &&
                                    updatedFields[updatedKey]?.current ===
                                      '-' && <IconChangeLogDeleted />}
                                </div>
                                <div
                                  className={
                                    styles.selectedFieldChangesSeparator
                                  }
                                />
                              </div>
                              <div className={styles.selectedFieldChangeValue}>
                                {updatedFields[updatedKey]?.current?.toString()}
                              </div>
                            </div>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                );
              }

              return objectArrayContent;
            })}
          {item.currentType === 'object' && (
            <div>
              {Object.keys(item?.current)?.map((key) => (
                <div
                  className={styles.selectedField}
                  key={`${item.name}-${key}`}
                >
                  <div className={styles.selectedFieldName}>
                    {`${item.name}: ${key}`}
                  </div>

                  <div className={styles.selectedFieldChangesWrapper}>
                    <div className={styles.selectedFieldChanges}>
                      <div className={styles.selectedFieldChangeValue}>
                        {item.prev &&
                        item.prev[key] &&
                        typeof item.prev[key] === 'object' ? (
                          <pre>{JSON.stringify(item.prev[key], null, 2)}</pre>
                        ) : (
                          <div>{item.prev[key]?.toString()}</div>
                        )}
                        {!item.prev && '-'}
                      </div>
                      <div
                        className={styles.selectedFieldChangesSeparatorWrapper}
                      >
                        <div className={styles.selectedFieldArrowWrapper}>
                          {item.prev &&
                            item.prev[key] &&
                            item.prev[key] !== '-' &&
                            item.prev[key].toString() &&
                            item.current &&
                            item.current[key].toString() && (
                              <IconChangeLogUpdated />
                            )}
                          {(!item.prev ||
                            (item.prev && !item.prev[key]) ||
                            (item.prev && item.prev[key] === '-')) &&
                            item.current.toString() && <IconChangeLogAdded />}
                          {item.prev &&
                            item.prev[key] &&
                            item.prev[key] !== '-' &&
                            !item.current[key].toString() && (
                              <IconChangeLogDeleted />
                            )}
                        </div>
                        <div className={styles.selectedFieldChangesSeparator} />
                      </div>
                      <div className={styles.selectedFieldChangeValue}>
                        {item.current &&
                        item.current[key] &&
                        typeof item.current[key] === 'object' ? (
                          <pre>
                            {JSON.stringify(item.current[key], null, 2)}
                          </pre>
                        ) : (
                          <div>{item.current[key]?.toString()}</div>
                        )}
                        {!item.current && '-'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
